import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { BikeComponent } from '@app/components/webform/calc/bike/bike.component';
import { CyberComponent } from '@app/components/webform/calc/cyber/cyber.component';
import { UvgComponent } from '@app/components/webform/calc/uvg/uvg.component';
import { formatNumber } from '@app/helpers/webform';
import { WebformService, KeyValue, CalcType } from '@app/services/webform.service';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { HouseholdGoodsComponent } from '@app/components/webform/calc/household-goods/household-goods.component';
import { MortgageComponent } from '@app/components/webform/calc/mortgage/mortgage.component';

@Component({
  standalone: false,
  selector: 'webform-calc',
  templateUrl: './calc.component.html',
  styleUrls: ['./calc.component.scss'],
})
export class CalcComponent implements OnInit, OnDestroy {
  @Input() element: any;
  @Input() callOnChange: boolean = true;
  @Input() action: Function | null = null;
  @Input() isSummary: boolean = false;
  @ViewChild('calc') calcComponent!:
    | UvgComponent
    | CyberComponent
    | BikeComponent
    | HouseholdGoodsComponent
    | MortgageComponent;

  formId!: string;
  plugin: CalcType = null;
  calcResult: any = null;
  loading: boolean = false;
  isValid: boolean = false;
  render: boolean = false;
  formData: KeyValue = {};
  taxOpen: boolean = false;
  taxCalculators: CalcType[] = ['bike_prime_calc', 'cyber_prime_calc'];
  formatNumber: Function = formatNumber;

  #formDataSub!: Subscription;
  #calcResultSub!: Subscription;

  readonly #platformId = inject<string>(PLATFORM_ID);
  readonly #webform = inject(WebformService);
  readonly #cdr = inject(ChangeDetectorRef);

  async ngOnInit(): Promise<void> {
    this.formId = this.element['#webform'];
    this.render = isPlatformBrowser(this.#platformId);
    this.#webform.setCalculatorElement(this.element);

    if (this.render) {
      this.plugin = this.element['#plugin'];
      if (this.callOnChange) {
        this.#formDataSub = this.#webform.formData
          .pipe(distinctUntilChanged(), debounceTime(300))
          .subscribe(async (v: KeyValue) => {
            if (JSON.stringify(v) === JSON.stringify(this.formData)) return;
            if (!v || !Object.keys(v).length) return;
            this.formData = v;

            if (this.element && this.element['#value']) {
              await this.call();
              this.#cdr.detectChanges();
            }
          });
      } else {
        this.#formDataSub = this.#webform.formData
          .pipe(distinctUntilChanged(), debounceTime(150))
          .subscribe(async (v: KeyValue) => (this.formData = v));
        this.#calcResultSub = this.#webform.calculatorResult
          .pipe(distinctUntilChanged(), debounceTime(150))
          .subscribe((v: KeyValue) => {
            if (!v || !Object.keys(v).length) return;
            this.calcResult = v;
            this.isValid = true;
            this.#cdr.detectChanges();
          });
      }
    }

    if (this.plugin === 'mortgage_calc') {
      await this.call();
    }
  }

  ngOnDestroy(): void {
    if (this.#formDataSub) this.#formDataSub.unsubscribe();
    if (this.#calcResultSub) this.#calcResultSub.unsubscribe();
  }

  get args(): string[] {
    return this.element['#args']?.split('|') || [];
  }

  get isDealer(): boolean {
    return this.formId?.includes('haendler') || this.formId.includes('dealer');
  }

  async calcHandler(): Promise<void> {
    if (this.#formDataSub) this.#formDataSub.unsubscribe();
  }

  async call(): Promise<void> {
    const args = this.formatArgs();
    try {
      const data = await this.#webform.call(`${this.element['#value']}${args}`);
      this.isValid = !!data;
      if (data) {
        this.isValid = !data.error;
        if (this.calcComponent) data['end_date'] = this.calcComponent.contractEnd;
        this.calcResult = data;
        this.#webform.setCalculatorResult(data);
      }
    } catch (e) {
      this.isValid = false;
    }
  }

  formatArgs(): string {
    let string = `?plugin=${this.plugin}`;
    this.args.some((arg: string) => {
      const argId: string = `${this.formId}--${arg}`;
      let values: string = '';
      if (this.formData[argId] && this.formData[argId] instanceof Object) {
        Object.keys(this.formData[argId]).forEach((key: string) => {
          if (this.formData[argId][key]) values += `${key},`;
        });
        if (values) string = `${string}&${arg}=${encodeURIComponent(values)}`;
      } else if (this.formData[argId]) {
        if (this.formData[argId] && this.formData[argId] !== 'null') {
          string = `${string}&${arg}=${encodeURIComponent(this.formData[argId])}`;
        }
      }
    });

    return string;
  }
}
